import {
  Heading,
  IconButton,
  SearchInput,
} from '@alefeducation/ui-elements-library-core';
import MultiSelect2 from '@alefeducation/ui-elements-library-core/lib/MultiSelect2';
import { MultiSelectOptionItem } from '@alefeducation/ui-elements-library-core/lib/components/MultiSelect2/MultiSelect2';
import { RedoAlt } from '@alefeducation/ui-elements-library-icons';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { EsisJobsInfoInputType, EsisLogsPropsType, JobInfo } from './types';
import { useEsisJobs } from './useEsisJobs';
import {
  columns,
  EsisJobType,
  esisJobTypeMap,
  JobTypes,
} from '../../utils/esisLogsUtils';
import DataGrid from '../../components/DataGrid/DataGrid';
import DatePicker from '../../components/DatePicker2/DatePicker';
import {
  dateAdjustmentEndDay,
  dateAdjustmentStartDay,
  getEndOfDayUtc,
  getStartOfDayUtc,
} from '../../utils/timeUtils';
import { TableContainer } from '../../components/DataGrid/StyledTable';

const initialFilters = {
  page: 1,
  size: 20,
  searchTerm: '',
  importType: [],
  startTime: '',
  endTime: '',
};

const EsisLogs = (props: EsisLogsPropsType) => {
  const [search, setSearch] = useState<string>('');
  const [filters, setFilters] = useState<EsisJobsInfoInputType>(initialFilters);
  const history = useHistory();

  const { esisJobsData, fetchAllJobs, isFetchLoaading } = useEsisJobs();

  useEffect(() => {
    fetchData(filters);
  }, [
    filters.page,
    filters.size,
    filters.searchTerm,
    filters.importType,
    filters.endTime,
  ]);

  const getJobTypes = () => {
    return JobTypes.map((job: EsisJobType) => ({
      id: esisJobTypeMap[job],
      type: job,
    }));
  };

  const onRecordFilterChange = (value: MultiSelectOptionItem[]) => {
    setFilters({
      ...filters,
      importType: value.map((type) => type.id as EsisJobType),
    });
  };

  const getSelectedJobTypes = () => {
    return filters.importType.map((job) => ({
      id: job,
      type: job,
    }));
  };

  const onSearchChange = (value: string) => {
    setFilters({
      ...filters,
      searchTerm: value,
    });
  };

  const datePickerOnChange = (from: Date | null, to: Date | null) => {
    setFilters({
      ...filters,
      startTime: from ? getStartOfDayUtc(from) : '',
      endTime: to ? getEndOfDayUtc(to) : '',
      page: 1,
    });
  };

  const fetchData = async (input: EsisJobsInfoInputType) => {
    fetchAllJobs({ variables: { ...input, page: input.page - 1 } });
  };

  const onPageChange = (page: number) => setFilters({ ...filters, page });
  const onPageSizeChange = (size: number) =>
    setFilters({ ...filters, page: 1, size });

  const refetchData = () => {
    fetchAllJobs({
      variables: { ...filters, page: filters.page - 1 },
    });
  };

  const allJobsType = getJobTypes();
  const selectedJobTypes = getSelectedJobTypes();

  return (
    <EsisLogsMain>
      <StyledContainer>
        <Heading variant="heading-small" color="primary.100">
          ESIS Logs
        </Heading>
      </StyledContainer>
      <StyledTable>
        <TopHeader>
          <Filters>
            <Select>
              <MultiSelect2
                label="Records"
                items={allJobsType}
                selectedItems={selectedJobTypes}
                textKey="type"
                data-testid="job-type"
                selectionChanged={onRecordFilterChange}
              />
            </Select>
            <DatePicker
              onChange={datePickerOnChange}
              startTime={dateAdjustmentStartDay(filters.startTime)}
              endTime={dateAdjustmentEndDay(filters.endTime)}
              minDate={
                new Date(new Date().setFullYear(new Date().getFullYear() - 1))
              }
              maxDate={new Date()}
              rangeStart={
                new Date(new Date().setMonth(new Date().getMonth() - 1))
              }
              showShortcuts
              showRange
            />
            <RefreshButton>
              <IconButton
                size="large"
                onClick={refetchData}
                tooltipPosition="bottom"
                tooltipContent={'Refresh'}
              >
                <RedoAlt color="#485465" />
              </IconButton>
            </RefreshButton>
          </Filters>
          <Search>
            <SearchInput
              width="400px"
              value={search}
              placeholder={'Search...'}
              onChange={(e) => {
                onSearchChange(e.target.value);
              }}
              onClear={() => {
                setSearch('');
              }}
              onSearch={() => {}}
            />
          </Search>
        </TopHeader>
        <TableContainer>
          <DataGrid<JobInfo>
            isDataLoading={isFetchLoaading}
            allowPagination
            data={esisJobsData?.items}
            columns={columns}
            page={filters?.page || 1}
            size={filters?.size || 20}
            pageCount={esisJobsData?.totalCount || 0}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClick={(item: any) => history.push(`/esis-logs/${item.id}`)}
          />
        </TableContainer>
      </StyledTable>
    </EsisLogsMain>
  );
};

export default EsisLogs;

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  height: 40px;
`;

export const EsisLogsMain = styled.div`
  height: 100%;
  font-family: 'Signika', sans-serif;
`;

const Filters = styled.div`
  display: flex;
  gap 16px;
`;

const Search = styled.div`
  padding-top: 27px;
`;

const Select = styled.div`
  width: 287px;
`;

const TopHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
`;

export const StyledTable = styled.div`
  background: ${({ theme }: any) => theme.colors.primary[0]};
  border-radius: 10px;
  padding: 16px 0px;
  height: 100%;

  .loading,
  .empty-message-view {
    height: 80%;
  }
`;

const RefreshButton = styled.div`
  padding-top: 27px;
`;
